import { createSelector } from 'reselect';
import PitchFxDataPack from '@gameday/components/PitchFx/store/reducers/PitchFxDataPack';
import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import getQueryParamOverride from '@gameday/utils/getQueryParamOverride';

const pitchFxConf = {
    hidePitchFXHover: true
};

const useContentApiOverride = getQueryParamOverride('useContentApi');
const getPitchFxData = (playData, game) => {
    const temp = new PitchFxDataPack(game, playData);
    return { ...temp, ...pitchFxConf };
};

const getTabs = (playData, fieldVisionODAvailable) => {
    const translate = useTranslate();
    const { highlights } = playData;
    const items = [];

    if (highlights) {
        items.push({
            text: 'Video',
            key: 'button_video'
        });
    }

    items.push({
        text: 'Pitch by Pitch',
        key: 'label_pitchbypitch'
    });

    if (fieldVisionODAvailable) {
        items.push({
            text: 'Gameday 3D',
            key: 'label_gameday3D'
        });
    }

    const itemsTranslated = items.map((item) => {
        const { key, text } = item;
        return {
            name: translate(key, text),
            type: text?.replace(/\s/g, '')?.toLowerCase()
        };
    });

    return itemsTranslated;
};

const getLanguage = (state) => state.config?.language;
const getDeviceInteraction = (state) => state.config?.deviceInteraction;
const getAllPlays = (state) => {
    return state?.atbat?.allPlays || [];
};
const getAllPlayMetrics = (state) => state?.atbat?.allPlayMetrics || [];
const getTeams = (state) => state?.atbat?.teams || {};
const getGamePK = (state) => state?.atbat?.gamePk || undefined;
const getSeason = (state) => state?.atbat?.season || undefined;
const getOfficialDate = (state) => state?.atbat?.officialDate || undefined;
const getLoading = (state) => state.loading?.isLoading;
const getGame = (state) => state?.atbat?.game ?? {};
const getVideoMap = (state) => {
    return (useContentApiOverride ?? state.config?.useContentApi)
        ? state.content?.playVideoMap
        : state.contentGraph?.playVideoMap;
};
const getLockView = (state) => state.lockview?.lockView;
const getAccessToken = (state) => state.userinfo?.accessToken;
const getIsLoggedIn = (state) => state.loggedIn?.isLoggedIn;
const getEnableFieldVisionOD = (state) => state.config?.enableFieldVisionOD;
const getForcedFieldVisionOn = (state) => state.config?.forceFieldVisionOn;
const getVenueId = (state) => state.gumbo?.gameData?.venue?.id;
const getGamedayType = (state) => state.gumbo?.gameData?.game?.gamedayType;
const getFieldVisionVenues = (state) => state.config?.fieldVisionVenues;
const getFieldVisionODDisablePriorToDate = (state) => state.config?.fieldVisionODDisablePriorToDate;

const getPlayData = (allPlays, videosMap, playIndex, allPlayMetrics) => {
    const playData = allPlays[playIndex];
    return {
        playMetrics: allPlayMetrics[playIndex],
        ...playData,
        ...{
            highlights: videosMap?.[playData?.resultPlayGuid] || null
        }
    };
};

const getUniforms = (state) => state?.uniforms || [];

export const getAtBatState = (playIndex) => {
    return createSelector(
        [
            getLanguage,
            getDeviceInteraction,
            getAllPlays,
            getAllPlayMetrics,
            getLoading,
            getVideoMap,
            getGamePK,
            getSeason,
            getOfficialDate,
            getGame,
            getTeams,
            getAccessToken,
            getIsLoggedIn,
            getLockView,
            getEnableFieldVisionOD,
            getForcedFieldVisionOn,
            getVenueId,
            getGamedayType,
            getFieldVisionVenues,
            getFieldVisionODDisablePriorToDate,
            getUniforms
        ],
        (
            language,
            deviceInteraction,
            allPlays,
            allPlayMetrics,
            isLoading,
            playVideoMap,
            gamePk,
            season,
            officialDate,
            game,
            teams,
            accessToken,
            isLoggedIn,
            lockView,
            enableFieldVisionOD,
            forcedFieldVisionOn,
            venueId,
            gamedayType,
            fieldVisionVenues,
            fieldVisionODDisablePriorToDate,
            uniforms

        ) => {
            const playData = getPlayData(allPlays, playVideoMap, playIndex, allPlayMetrics);
            const pitchFxData = getPitchFxData(playData, game);
            const homeOrAway = pitchFxData?.atbat?.homeAway;
            const batterModel = {
                ...pitchFxData,
                atbat: {
                    ...pitchFxData.atbat,
                    uniformData: uniforms[homeOrAway]
                }
            };
            const currentUniforms = uniforms?.[homeOrAway];
            const doesMannequinDataExistForFVOD = officialDate >= fieldVisionODDisablePriorToDate || fieldVisionODDisablePriorToDate === undefined; // If we blank out the official date in contenful, lets consider this check 'turned off'
            const fieldVisionODAvailable = enableFieldVisionOD && doesMannequinDataExistForFVOD && playData?.highlights && season >= 2022 && gamedayType === 'P' && fieldVisionVenues && fieldVisionVenues.has('' + venueId);
            const tabItems = getTabs(playData, (fieldVisionODAvailable || forcedFieldVisionOn)) || [];
            return {
                playData,
                isLoading,
                language,
                deviceInteraction,
                gamePk,
                season,
                items: tabItems,
                batterModel,
                teams,
                venueId,
                accessToken,
                isLoggedIn,
                lockView,
                fieldVisionODAvailable,
                forcedFieldVisionOn,
                currentUniforms
            };
        }
    );
};
